import { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  PaginationState,
} from "@tanstack/react-table";
import { Sighting } from "../../../models/SherlockModel";
import { useAppDispatch, useAppSelector } from "../../../redux/hook";
import { getPagedSherlockData } from "../../../service/apiAccessLogic";
import { setPagedSightings } from "../../../redux/sherlockSlice";
import { msalInstance } from "../../..";

type DataTableProps = {
  tableItems: Sighting[];
  streamItems: Sighting[];
  loading?: boolean;
  animateNewItems?: boolean;
  newItemIds?: string[];
};

const columnHelper = createColumnHelper<Sighting>();

const DataTable = ({ tableItems, streamItems, loading, animateNewItems = false }: DataTableProps) => {
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  const [previousItemIds, setPreviousItemIds] = useState<Set<string>>(new Set());

  const pageEnd = useAppSelector(state => state.sherlock.pagedSightingsEnd)
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25
  })

  const pagination = useMemo(() => ({
    pageIndex,
    pageSize
  }), [pageIndex, pageSize])

  const dispatch = useAppDispatch();

  const tableItemsMerged = [...tableItems, ...streamItems].slice(0, 500);
  tableItemsMerged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const ids = new Set();
  const items = tableItemsMerged.filter((item) => {
    if (ids.has(item.id)) {
      return false;
    } else {
      ids.add(item.id);
      return true;
    }
  });

  const paginatedSightings = items.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  useEffect(() => {
    if (animateNewItems) {
      const currentItemIds = new Set(streamItems.map(item => item.id));

      if (previousItemIds.size > 0) {
        const newItemIds = streamItems
          .filter(item => !previousItemIds.has(item.id))
          .map(item => item.id);

        if (newItemIds.length > 0) {
          setAnimatingIds(new Set(newItemIds));

          // Remove animation after 2 seconds
          const timer = setTimeout(() => {
            setAnimatingIds(new Set());
          }, 2000);

          setTimeout(() => clearTimeout(timer), 0);
        }
      }

      setPreviousItemIds(currentItemIds);
    }
  }, [tableItems, streamItems, animateNewItems]);

  const processSightingIndicator = (indicator: string) => {
    const underscoreIndex = indicator.indexOf("_");
    if (underscoreIndex !== -1) {
      return indicator.substring(0, underscoreIndex);
    }
    return indicator;
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("indicator", {
        header: "Indicator",
        cell: (info) => (
          <div className="whitespace-nowrap">{atob(processSightingIndicator(info.getValue()))}</div>
        ),
      }),
      columnHelper.accessor("date", {
        header: "Date",
        cell: (info) => {
          const date = info.getValue();
          return date.replace("T", " ").slice(0, date.indexOf("."));
        },
      }),
      columnHelper.accessor("organisation_name", {
        header: "Organisation",
      }),
      columnHelper.display({
        id: "client",
        header: "Client",
        cell: (info) => {
          const row = info.row.original;
          return ("client_name" in row && (row as any).client_name) || "—";
        },
      }),
      columnHelper.accessor("platform", {
        header: "Platform",
      }),
    ],
    []
  );

  const table = useReactTable({
    data: paginatedSightings,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(items.length / pageSize),
    state: { pagination },
    onPaginationChange: setPagination,
    manualPagination: true,
  });

  useEffect(() => {
    if (pageEnd) return
    if (tableItems.length === pageSize || (pageIndex + 1) * pageSize === tableItems.length) {
      if (tableItems.length === 0) return
      getPagedSherlockData(msalInstance, "sightings", tableItems[tableItems.length - 1]["_ts"], tableItems[tableItems.length - 1]["id"])
        .then((response) => dispatch(setPagedSightings(response.data)))
        .catch((err) => { console.error("Error fetching sightings", err) })
    }
  }, [pageIndex, pageSize, tableItems, dispatch, pageEnd])

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-8 w-full animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="h-32 grid place-items-center text-sm text-gray-500">
        No results for this range
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes highlight-glow {
          0% { background-color: rgb(34, 197, 94, 0.3); }
          50% { background-color: rgb(34, 197, 94, 0.6); }
          100% { background-color: transparent; }
        }
        .animate-highlight {
          animation: highlight-glow 2s ease-out;
        }
      `}</style>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left text-gray-600">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="py-2 pr-4">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isAnimating = animatingIds.has(row.original.id || row.id);
              return (
                <tr
                  key={row.id}
                  className={`border-t ${isAnimating ? 'animate-highlight' : ''}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="py-2 pr-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {'<<'}
          </button>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {'<'}
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {'>'}
          </button>
          <button
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {'>>'}
          </button>
        </div>

        <span className="text-sm text-gray-600">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </span>

        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="px-2 py-1 text-sm border rounded"
        >
          {[25, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default DataTable;
